import { useEffect, useId, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, CameraOff } from "lucide-react";
import { Button } from "./ui/button";


export const QrScannerPanel = ({ onDetected }) => {
  const elementId = useId().replace(/:/g, "-");
  const [scannerState, setScannerState] = useState({ active: false, loading: false, error: "" });

  useEffect(() => {
    let html5QrCode;
    let cancelled = false;

    const startScanner = async () => {
      if (!scannerState.active) return;
      setScannerState((current) => ({ ...current, loading: true, error: "" }));
      try {
        html5QrCode = new Html5Qrcode(elementId);
        await html5QrCode.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => {
            if (cancelled) return;
            onDetected(decodedText);
            setScannerState((current) => ({ ...current, active: false }));
          },
          undefined,
        );
        if (!cancelled) {
          setScannerState((current) => ({ ...current, loading: false, error: "" }));
        }
      } catch (error) {
        if (!cancelled) {
          setScannerState({ active: false, loading: false, error: error?.message || "Camera access failed" });
        }
      }
    };

    startScanner();

    return () => {
      cancelled = true;
      if (html5QrCode?.isScanning) {
        html5QrCode.stop().catch(() => undefined);
      }
      html5QrCode?.clear().catch(() => undefined);
    };
  }, [elementId, onDetected, scannerState.active]);

  return (
    <div className="space-y-4" data-testid="employee-qr-scanner-panel">
      <div className="flex flex-wrap gap-3" data-testid="employee-qr-scanner-actions">
        <Button
          type="button"
          className="rounded-2xl bg-[#0D1B2A] text-white hover:bg-[#08131F]"
          onClick={() => setScannerState((current) => ({ ...current, active: true }))}
          disabled={scannerState.loading || scannerState.active}
          data-testid="employee-qr-scanner-start-button"
        >
          <Camera className="mr-2 h-4 w-4" />
          {scannerState.loading ? "Starting camera..." : scannerState.active ? "Camera ready" : "Start camera scanner"}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="rounded-2xl"
          onClick={() => setScannerState({ active: false, loading: false, error: "" })}
          disabled={!scannerState.active && !scannerState.loading}
          data-testid="employee-qr-scanner-stop-button"
        >
          <CameraOff className="mr-2 h-4 w-4" />
          Stop scanner
        </Button>
      </div>

      <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-4" data-testid="employee-qr-scanner-frame">
        <div id={elementId} className="min-h-[280px]" data-testid="employee-qr-scanner-reader" />
      </div>

      {scannerState.error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="employee-qr-scanner-error">
          {scannerState.error}
        </div>
      ) : null}
    </div>
  );
};